/**
 * Session-scoped OPC import failures, keyed by project. The Schedules in OPC
 * page writes here when an import fails before the refetch lands with FAILED on
 * the row; the Project Schedule sub-tab row reads it so the failure dot can
 * appear without waiting on that refetch (and while the user is on Schedules).
 *
 * Leaf module — no imports — so the page and the layout can share it without
 * cycles.
 */

type Listener = () => void;

let byProject: Record<string, ReadonlySet<string>> = {};
const listeners = new Set<Listener>();

const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>): boolean =>
  a.size === b.size && [...a].every((id) => b.has(id));

const emit = () => {
  listeners.forEach((cb) => {
    try {
      cb();
    } catch {
      // a failing listener must not stop the others
    }
  });
};

export function getOpcSessionImportFailures(
  projectId: string
): ReadonlySet<string> {
  return byProject[projectId] ?? new Set();
}

export function setOpcSessionImportFailures(
  projectId: string,
  ids: ReadonlySet<string>
): void {
  const prev = byProject[projectId] ?? new Set();
  if (sameSet(prev, ids)) return;
  byProject = { ...byProject, [projectId]: new Set(ids) };
  emit();
}

export function onOpcSessionImportFailuresChange(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
